import type { CompanyType } from "@entrilia/shared/api/clientTypes";
import {
  combineValidators,
  regexValidator,
  requiredValidator,
  websiteUrlValidator,
} from "@entrilia/shared/utilities/validators";
import type { CompanyInfo, CopyCompanyRequest } from "../../../api/clientsManagementTypes";
import type { FormValidationResults } from "../../../utilities/forms";

export interface CopyCompanyForm {
  sourceClientCode: string;
  targetClientCode: string;
  createNewClient: boolean;
  newClientTitle: string;
  newClientCompanyType: CompanyType;
  newClientWebsiteUrl: string;
  enableInvestorPortal: boolean;
  enableExpenseManagement: boolean;
  enablePortfolioMonitoringDataCollection: boolean;
  createNewCompany: boolean;
  targetBcCompanyId: string;
  targetBcCompanyName: string;
  targetBcCompanyDisplayName: string;
  validationResults: FormValidationResults<ValidationKey>;
}

type ValidationKey =
  | "sourceClientCode"
  | "targetClientCode"
  | "newClientTitle"
  | "newClientWebsiteUrl"
  | "targetBcCompanyId"
  | "targetBcCompanyName"
  | "targetBcCompanyDisplayName";

export const getInitialCopyCompanyForm = (): CopyCompanyForm => ({
  sourceClientCode: "",
  targetClientCode: "",
  createNewClient: false,
  newClientTitle: "",
  newClientCompanyType: "Production",
  newClientWebsiteUrl: "",
  enableInvestorPortal: false,
  enableExpenseManagement: false,
  enablePortfolioMonitoringDataCollection: false,
  createNewCompany: true,
  targetBcCompanyId: "",
  targetBcCompanyName: "",
  targetBcCompanyDisplayName: "",
  validationResults: {},
});

const newClientCodeValidator = combineValidators(
  requiredValidator,
  regexValidator(/^[a-z0-9]+$/, "Only lowercase letters and numbers are allowed")
);

export const validateCopyCompanyForm = (form: CopyCompanyForm): FormValidationResults<ValidationKey> => {
  if (form.createNewClient) {
    return {
      sourceClientCode: requiredValidator(form.sourceClientCode),
      targetClientCode: newClientCodeValidator(form.targetClientCode),
      newClientTitle: requiredValidator(form.newClientTitle),
      newClientWebsiteUrl: websiteUrlValidator()(form.newClientWebsiteUrl),
      targetBcCompanyName: requiredValidator(form.targetBcCompanyName),
      targetBcCompanyDisplayName: requiredValidator(form.targetBcCompanyDisplayName),
    };
  }

  if (form.createNewCompany) {
    return {
      sourceClientCode: requiredValidator(form.sourceClientCode),
      targetClientCode: requiredValidator(form.targetClientCode),
      targetBcCompanyName: requiredValidator(form.targetBcCompanyName),
      targetBcCompanyDisplayName: requiredValidator(form.targetBcCompanyDisplayName),
    };
  }

  return {
    sourceClientCode: requiredValidator(form.sourceClientCode),
    targetClientCode: requiredValidator(form.targetClientCode),
    targetBcCompanyId: requiredValidator(form.targetBcCompanyId),
  };
};

export const mapCopyCompanyFormToRequest = (form: CopyCompanyForm): CopyCompanyRequest => ({
  sourceClientCode: form.sourceClientCode.trim(),
  targetClientCode: form.targetClientCode.trim(),
  targetBcCompanyName: form.targetBcCompanyName.trim(),
  targetBcCompanyDisplayName: form.targetBcCompanyDisplayName.trim(),
  targetBcCompanyId: form.createNewClient || form.createNewCompany ? undefined : form.targetBcCompanyId,
  newClient: form.createNewClient
    ? {
        title: form.newClientTitle.trim(),
        companyType: form.newClientCompanyType,
        enableInvestorPortal: form.enableInvestorPortal,
        enableExpenseManagement: form.enableExpenseManagement,
        enablePortfolioMonitoringDataCollection: form.enablePortfolioMonitoringDataCollection,
        websiteUrl: form.newClientWebsiteUrl.trim() || undefined,
      }
    : undefined,
});

// Prefills the new-client settings from the source client so the copy starts as a faithful clone.
export const applySourceClientDefaults = (
  form: CopyCompanyForm,
  sourceClient: CompanyInfo | undefined
): CopyCompanyForm =>
  sourceClient
    ? {
        ...form,
        newClientCompanyType: sourceClient.companyType,
        enableInvestorPortal: sourceClient.enableInvestorPortal,
        enableExpenseManagement: sourceClient.enableExpenseManagement,
        enablePortfolioMonitoringDataCollection: sourceClient.enablePortfolioMonitoringDataCollection,
      }
    : form;
